import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePlanFromWizardRequestDto,
  PreviewPlanFromWizardRequestDto,
  UpdatePlanRequestDto,
} from '@evimo/client/models';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import { PreventiveMaintenanceService } from '~/app/services/preventive-maintenance/preventive-maintenance.service';

export function usePlans() {
  const { user } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const organizationId = user?.organizationId ?? '';
  const queryClient = useQueryClient();
  const scopeKey = isSuperAdmin ? 'all-orgs' : organizationId;

  const query = useQuery({
    queryKey: ['preventive-maintenance', 'plans', scopeKey],
    queryFn: async () => {
      const res = await PreventiveMaintenanceService.listPlans();
      return res.data;
    },
    enabled: isSuperAdmin || !!organizationId,
  });

  const wizardMutation = useMutation({
    mutationFn: (dto: CreatePlanFromWizardRequestDto) =>
      PreventiveMaintenanceService.createPlanFromWizard(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['preventive-maintenance', 'plans'],
      });
    },
  });

  const previewMutation = useMutation({
    mutationFn: (dto: PreviewPlanFromWizardRequestDto) =>
      PreventiveMaintenanceService.previewPlanFromWizard(dto),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePlanRequestDto }) =>
      PreventiveMaintenanceService.updatePlan(id, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['preventive-maintenance', 'plans'],
      });
    },
  });

  return {
    data: query.data ?? [],
    plans: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    createPlanFromWizard: wizardMutation.mutateAsync,
    isCreatingWizard: wizardMutation.isPending,
    previewPlanFromWizard: previewMutation.mutateAsync,
    isPreviewingWizard: previewMutation.isPending,
    previewResult: previewMutation.data,
    updatePlan: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}

export function usePlanDetail(planId: string | undefined) {
  const { user } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const organizationId = user?.organizationId ?? '';
  const scopeKey = isSuperAdmin ? 'all-orgs' : organizationId;

  const query = useQuery({
    queryKey: ['preventive-maintenance', 'plans', planId, scopeKey],
    queryFn: () => PreventiveMaintenanceService.getPlanById(planId!),
    enabled: (isSuperAdmin || !!organizationId) && !!planId,
  });

  return {
    plan: query.data,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
